import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, Users, LogOut, Menu, Loader2, CheckCircle2,
  LayoutDashboard, BarChart3, Workflow, Activity, Store, Shield,
  Settings, ArrowRight, XCircle, RefreshCw
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [syncLog, setSyncLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configForm, setConfigForm] = useState({});
  const [testing, setTesting] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [pRes, cRes, lRes] = await Promise.all([
        axios.get(`${API}/crm-adapter/providers`),
        axios.get(`${API}/crm-adapter/config`),
        axios.get(`${API}/crm-adapter/sync-log?limit=10`),
      ]);
      setProviders(pRes.data);
      setConfigs(cRes.data);
      setSyncLog(lRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleConfigure = async (providerId) => {
    const form = configForm[providerId] || {};
    try {
      await axios.post(`${API}/crm-adapter/configure`, { provider: providerId, ...form });
      toast.success(`${providerId} configured`);
      fetchAll();
    } catch (err) { toast.error('Configuration failed'); }
  };

  const handleTest = async (providerId) => {
    setTesting(providerId);
    try {
      const res = await axios.post(`${API}/crm-adapter/test?provider=${providerId}`);
      if (res.data.status === 'connected') {
        toast.success(`${providerId}: Connected`);
      } else {
        toast.error(`${providerId}: ${res.data.message || 'Failed'}`);
      }
    } catch { toast.error('Test failed'); }
    finally { setTesting(''); }
  };

  const isConfigured = (providerId) => configs.some(c => c.provider === providerId);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: Shield, label: 'Operations', path: '/operations' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
    { icon: Settings, label: 'CRM Settings', path: '/crm-settings' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3"><div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p></div></div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="crm-settings-title">CRM Integrations</h1>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 max-w-4xl space-y-6">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
            <>
              {/* Provider Cards */}
              <div className="space-y-4" data-testid="provider-list">
                {providers.map(p => {
                  const configured = isConfigured(p.id);
                  return (
                    <div key={p.id} className={`bg-[#0a0a0a] border p-6 ${configured ? 'border-emerald-500/20' : 'border-white/5'}`} data-testid={`provider-${p.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">{p.name}</h3>
                            {configured && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => handleTest(p.id)} disabled={testing === p.id} variant="outline" size="sm" className="border-white/10 rounded-sm h-8 text-xs" data-testid={`test-${p.id}`}>
                            {testing === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Test
                          </Button>
                        </div>
                      </div>

                      {p.setup_required && (
                        <div className="space-y-3 border-t border-white/5 pt-4">
                          {p.fields.map(field => (
                            <div key={field.key}>
                              <label className="text-xs text-muted-foreground uppercase mb-1 block">{field.label}</label>
                              <div className="flex gap-2">
                                <Input
                                  type="password"
                                  placeholder={field.help}
                                  value={configForm[p.id]?.[field.key] || ''}
                                  onChange={e => setConfigForm(prev => ({ ...prev, [p.id]: { ...prev[p.id], [field.key]: e.target.value } }))}
                                  className="bg-black/50 border-white/10 rounded-sm text-sm flex-1"
                                  data-testid={`input-${p.id}-${field.key}`}
                                />
                              </div>
                            </div>
                          ))}
                          <Button onClick={() => handleConfigure(p.id)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid={`configure-${p.id}`}>
                            Save Configuration
                          </Button>
                        </div>
                      )}

                      {!p.setup_required && (
                        <div className="border-t border-white/5 pt-3 mt-3">
                          <p className="text-xs text-emerald-400">Always available — no configuration needed</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Sync Log */}
              {syncLog.length > 0 && (
                <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="sync-log">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Recent Sync Activity</h3>
                  <div className="space-y-2">
                    {syncLog.map(log => (
                      <div key={log.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 text-xs">
                        <div className="flex items-center gap-2">
                          {log.status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                          <span className="font-mono text-muted-foreground">{log.operation}</span>
                          <span className="px-1.5 py-0.5 bg-white/5 text-muted-foreground text-[10px]">{log.provider}</span>
                        </div>
                        <span className="text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default CRMSettings;
